export type DecimalString = string;
export type ConnectionState = "live" | "reconnecting" | "stale";
export type BrokerId = "tinvest" | "finam" | "ibkr";
export type CandleInterval =
  | "1m"
  | "5m"
  | "10m"
  | "1h"
  | "4h"
  | "1d"
  | "1w"
  | "1mo";

export const CANDLE_INTERVALS: readonly CandleInterval[] = [
  "1m",
  "5m",
  "10m",
  "1h",
  "4h",
  "1d",
  "1w",
  "1mo",
];

export const INSTRUMENT_SEARCH_KINDS = [
  "INSTRUMENT_TYPE_SHARE",
  "INSTRUMENT_TYPE_BOND",
  "INSTRUMENT_TYPE_FUTURES",
  "INSTRUMENT_TYPE_ETF",
  "INSTRUMENT_TYPE_CURRENCY",
] as const;

export type InstrumentSearchKind = typeof INSTRUMENT_SEARCH_KINDS[number];

export interface AccountDto {
  broker: BrokerId;
  id: string;
  key: string;
  name: string;
  type: string;
}

export interface QuoteDto {
  changePercent?: number;
  closePrice?: DecimalString;
  instrumentUid: string;
  price?: DecimalString;
  time?: string;
}

export interface InstrumentDto {
  assetUid?: string;
  brandColor?: string;
  brandLogoName?: string;
  brandTextColor?: string;
  classCode?: string;
  country?: string;
  currency?: string;
  exchange?: string;
  instrumentType: string;
  isin?: string;
  lot: number;
  name: string;
  ticker: string;
  uid: string;
}

export interface PortfolioPositionDto {
  averagePrice?: DecimalString;
  blockedQuantity?: DecimalString;
  currentPrice?: DecimalString;
  dailyChange?: DecimalString;
  dailyChangePercent?: number;
  expectedYield?: DecimalString;
  expectedYieldPercent?: number;
  instrument: InstrumentDto;
  quantity: DecimalString;
  value?: DecimalString;
  varMargin?: DecimalString;
}

export interface PortfolioDto {
  blocked?: DecimalString;
  currency: string;
  dailyYield?: DecimalString;
  dailyYieldPercent?: number;
  expectedYield?: DecimalString;
  expectedYieldPercent?: number;
  groupTotals: Record<string, DecimalString>;
  positions: PortfolioPositionDto[];
  totalAmount?: DecimalString;
  updatedAt: string;
  varMargin?: DecimalString;
}

export interface OrderDto {
  createdAt?: string;
  direction: string;
  executedLots: DecimalString;
  id: string;
  instrument: InstrumentDto;
  lots: DecimalString;
  price?: DecimalString;
  status: string;
  type: string;
}

export interface OperationDto {
  date: string;
  description: string;
  id: string;
  instrumentUid?: string;
  payment?: DecimalString;
  quantity?: DecimalString;
  state: string;
  type: string;
  currency?: string;
}

export interface OrderBookLevelDto {
  price: DecimalString;
  quantity: DecimalString;
}

export interface OrderBookDto {
  asks: OrderBookLevelDto[];
  bids: OrderBookLevelDto[];
  closePrice?: DecimalString;
  instrumentUid: string;
  lastPrice?: DecimalString;
  limitDown?: DecimalString;
  limitUp?: DecimalString;
  time?: string;
}

export interface CandleDto {
  close: DecimalString;
  complete: boolean;
  high: DecimalString;
  instrumentUid: string;
  interval: CandleInterval;
  low: DecimalString;
  open: DecimalString;
  time: string;
  volume: DecimalString;
}

export interface MarketIndicatorDto {
  changePercent?: number;
  instrumentUid?: string;
  key: string;
  label: string;
  price?: DecimalString;
  ticker?: string;
}

export interface GatewayStatusDto {
  error?: string;
  lastMessageAt?: string;
  state: ConnectionState;
  streams: Record<string, ConnectionState>;
  updatedAt: string;
}

export interface BootstrapDto {
  brokerWarnings?: string[];
  ibkrLoginAvailable?: boolean;
  account: AccountDto;
  accounts: AccountDto[];
  candles: CandleDto[];
  favorites: InstrumentDto[];
  indicators: MarketIndicatorDto[];
  interval: CandleInterval;
  operations: OperationDto[];
  orderBook?: OrderBookDto;
  orders: OrderDto[];
  portfolio: PortfolioDto;
  quotes: Record<string, QuoteDto>;
  selectedInstrument?: InstrumentDto;
  status: GatewayStatusDto;
}

export type ClientWsMessage =
  | {
      type: "selection.set";
      instrumentUid: string;
      interval: CandleInterval;
    }
  | {
      type: "watchlist.set";
      instrumentUids: string[];
    };

export type ServerWsMessage =
  | ({ type: "connection.status" } & GatewayStatusDto)
  | { type: "market.quote"; quote: QuoteDto }
  | { type: "market.orderbook"; orderBook: OrderBookDto }
  | { type: "market.candle"; candle: CandleDto }
  | { type: "portfolio.snapshot"; portfolio: PortfolioDto }
  | { type: "orders.snapshot"; orders: OrderDto[] }
  | { type: "operations.snapshot"; operations: OperationDto[] }
  | {
      type: "selection.changed";
      instrumentUid: string;
      interval: CandleInterval;
    }
  | { type: "error"; code: string };

export function isCandleInterval(value: unknown): value is CandleInterval {
  return typeof value === "string" && CANDLE_INTERVALS.includes(value as CandleInterval);
}

export function isInstrumentSearchKind(value: unknown): value is InstrumentSearchKind {
  return typeof value === "string" && INSTRUMENT_SEARCH_KINDS.includes(value as InstrumentSearchKind);
}
